// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Button,
    Menu,
    MenuDivider,
    MenuList,
    MenuPopover,
    MenuTrigger,
    TableCellLayout,
} from '@fluentui/react-components';
import {
    ArrowDownloadRegular,
    BookRegular,
    CloudDismissRegular,
    CloudRegular,
    DeleteRegular,
    HatGraduationRegular,
    MoreHorizontalRegular,
} from '@fluentui/react-icons';
import { CurriculumState } from '@microsoft/vscodeedu-api';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { GitHubLogo } from '../../icons/sign-in';
import { useAppStyles } from '../../styles';
import { IntlProps, TrackedMenuItem } from '../common';

// Actions cell properties.
export type ActionsCellProps = IntlProps<{
    state: CurriculumState;
    courseLinkUrl?: string;
    courseId?: string;
    repoName?: string;
    onPublish: () => void;
    onUnpublish: () => void;
    onDelete: () => void;
}>;

// Actions data grid cell contents.
export const ActionsCell = injectIntl((props: ActionsCellProps) => {
    const { intl, state, courseLinkUrl, courseId, repoName, onPublish, onUnpublish, onDelete } = props;
    const styles = useAppStyles();
    const navigate = useNavigate();
    return (
        <TableCellLayout>
            <Menu>
                <MenuTrigger>
                    <Button
                        appearance="subtle"
                        icon={<MoreHorizontalRegular />}
                        title={intl.formatMessage({
                            defaultMessage: 'Actions',
                            description: 'Menu button icon aria label',
                        })}
                    />
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        {(state === 'draft' || state === 'published') && (
                            <TrackedMenuItem trackAction="publish" icon={<CloudRegular />} onClick={onPublish}>
                                {state === 'draft' && (
                                    <FormattedMessage defaultMessage="Publish" description="Menu item text" />
                                )}
                                {state === 'published' && (
                                    <FormattedMessage defaultMessage="Republish" description="Menu item text" />
                                )}
                            </TrackedMenuItem>
                        )}
                        {state === 'published' && (
                            <>
                                <TrackedMenuItem
                                    trackAction="unpublish"
                                    icon={<CloudDismissRegular />}
                                    onClick={onUnpublish}
                                >
                                    <FormattedMessage defaultMessage="Unpublish" description="Menu item text" />
                                </TrackedMenuItem>
                                <MenuDivider />
                            </>
                        )}
                        {state === 'published' && courseLinkUrl !== undefined && (
                            <TrackedMenuItem
                                trackAction="open-as-student"
                                icon={<HatGraduationRegular />}
                                onClick={() => window.open(courseLinkUrl, '_blank', 'noopener')}
                            >
                                <FormattedMessage defaultMessage="Open as student" description="Menu item text" />
                            </TrackedMenuItem>
                        )}
                        {state === 'published' && courseLinkUrl !== undefined && (
                            <TrackedMenuItem
                                trackAction="course-info"
                                icon={<BookRegular />}
                                onClick={() => navigate(`/courses/${courseId}`)}
                            >
                                <FormattedMessage defaultMessage="View course info" description="Menu item text" />
                            </TrackedMenuItem>
                        )}
                        {repoName !== undefined && (
                            <TrackedMenuItem
                                trackAction="open-github"
                                icon={<GitHubLogo />}
                                onClick={() => window.open(`https://github.com/${repoName}`, '_blank', 'noopener')}
                            >
                                <FormattedMessage defaultMessage="Open GitHub repo" description="Menu item text" />
                            </TrackedMenuItem>
                        )}
                        <MenuDivider />
                        {repoName !== undefined && (
                            <TrackedMenuItem
                                trackAction="download"
                                icon={<ArrowDownloadRegular />}
                                onClick={() =>
                                    window.open(
                                        `https://github.com/${repoName}/archive/refs/heads/main.zip`,
                                        '_blank',
                                        'noopener'
                                    )
                                }
                            >
                                <FormattedMessage defaultMessage="Download" description="Menu item text" />
                            </TrackedMenuItem>
                        )}
                        <TrackedMenuItem
                            trackAction="delete"
                            icon={<DeleteRegular />}
                            className={styles.menuDanger}
                            onClick={onDelete}
                        >
                            <FormattedMessage defaultMessage="Delete" description="Menu item text" />
                        </TrackedMenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>
        </TableCellLayout>
    );
});

export default ActionsCell;
